import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { CheckCircle2, AlertTriangle, RefreshCw, PlayCircle, ShieldCheck, TrendingUp, Activity, Wallet } from 'lucide-react';
import AdminSSOTDriftWatchlist from './AdminSSOTDriftWatchlist';
import AdminFinancialIntegrityPanel from './AdminFinancialIntegrityPanel';

const StatusChip = ({ status }) => {
  const map = {
    consistent: { label: 'In sync', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    diverged: { label: 'Diverged', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    no_variants: { label: '—', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  };
  const m = map[status] || map.no_variants;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}>
      {m.label}
    </span>
  );
};

const SumsTable = ({ sums }) => {
  const entries = Object.entries(sums || {});
  const values = entries.map(([, v]) => v).filter((v) => v !== null);
  const diverged = values.length > 1 && new Set(values).size > 1;
  const maxV = diverged ? Math.max(...values) : null;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
      {entries.map(([path, v]) => (
        <div key={path} className="flex justify-between border-b border-gray-100 py-0.5">
          <span className="text-gray-500">{path}</span>
          <span
            className={
              v === null
                ? 'text-gray-300'
                : diverged && v === maxV
                ? 'text-emerald-600 font-bold'
                : diverged
                ? 'text-rose-600'
                : 'text-gray-700'
            }
          >
            {v === null ? '—' : v}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function AdminSSOTAudit() {
  const { apiCall } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [healing, setHealing] = useState(false);
  const [includeConsistent, setIncludeConsistent] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [healReport, setHealReport] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        apiCall('/api/admin/ssot-audit/variant-combinations'),
        apiCall(
          `/api/admin/ssot-audit/variant-combinations/diff?include_consistent=${includeConsistent}&limit=300`
        ),
      ]);
      setSummary(s);
      setRows(d.rows || []);
    } catch (e) {
      toast.error('Failed to load SSOT audit');
    } finally {
      setLoading(false);
    }
  }, [apiCall, includeConsistent]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const runHeal = async (dryRun) => {
    setHealing(true);
    try {
      const res = await apiCall(
        `/api/admin/ssot-audit/variant-combinations/heal?dry_run=${dryRun}`,
        { method: 'POST' }
      );
      setHealReport(res);
      if (!dryRun) {
        toast.success(`Reconciled ${res.healed} listing${res.healed === 1 ? '' : 's'}`);
        fetchAll();
      } else {
        toast.info(`Dry run — ${res.healed} listing${res.healed === 1 ? '' : 's'} would be reconciled`);
      }
    } catch (e) {
      toast.error('Heal run failed');
    } finally {
      setHealing(false);
      setConfirmOpen(false);
    }
  };

  const healthyPct = summary?.healthy_percentage ?? 0;
  const divergedCount = summary?.diverged ?? 0;

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get('tab');
    if (t === 'drift') return 'drift';
    if (t === 'financial') return 'financial';
    return 'variants';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const handleTabChange = (val) => {
    setActiveTab(val);
    const next = new URLSearchParams(searchParams);
    if (val === 'variants') next.delete('tab');
    else next.set('tab', val);
    setSearchParams(next, { replace: true });
  };

  return (
    <DashboardLayout>
    <div className="p-6 max-w-7xl mx-auto" data-testid="ssot-audit-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-600" />
            SSOT Audit
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Single Source of Truth health checks across the platform. Spot variant-stock
            drift across the four canonical paths, and reconcile every order's
            Shopify ↔ Invoice ↔ Razorpay ↔ Payout ladder under the Financial Drift tab.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList data-testid="ssot-audit-tabs">
          <TabsTrigger value="variants" data-testid="ssot-tab-variants">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
            Variant Combinations
          </TabsTrigger>
          <TabsTrigger value="drift" data-testid="ssot-tab-drift">
            <Activity className="h-3.5 w-3.5 mr-1.5" />
            Financial Drift
          </TabsTrigger>
          <TabsTrigger value="financial" data-testid="ssot-tab-financial">
            <Wallet className="h-3.5 w-3.5 mr-1.5" />
            Financial Integrity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="variants" className="mt-6">
          <div className="flex items-start justify-between mb-4">
            <p className="text-sm text-gray-500 max-w-2xl">
              Detects listings where the four copies of <code className="bg-gray-100 px-1 rounded">variant_combinations</code>
              {' '}(root snake, root camel, nested snake, nested camel) hold different stock totals.
              Safe to run anytime — heal picks the highest credible value and fans it to all paths.
            </p>
            <Button
              variant="outline"
              onClick={fetchAll}
              disabled={loading}
              data-testid="ssot-audit-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card data-testid="ssot-tile-total">
          <CardHeader className="pb-2">
            <CardDescription>Total listings</CardDescription>
            <CardTitle className="text-3xl">{summary?.total_listings ?? '—'}</CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="ssot-tile-consistent" className="border-emerald-100">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-emerald-500" /> In sync
            </CardDescription>
            <CardTitle className="text-3xl text-emerald-700">
              {summary?.consistent ?? '—'}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card
          data-testid="ssot-tile-diverged"
          className={divergedCount > 0 ? 'border-rose-200 bg-rose-50/30' : 'border-gray-100'}
        >
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <AlertTriangle className={`h-3 w-3 ${divergedCount > 0 ? 'text-rose-500' : 'text-gray-400'}`} />{' '}
              Diverged
            </CardDescription>
            <CardTitle className={`text-3xl ${divergedCount > 0 ? 'text-rose-700' : 'text-gray-400'}`}>
              {summary?.diverged ?? '—'}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card data-testid="ssot-tile-healthy-pct">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-indigo-500" /> Health score
            </CardDescription>
            <CardTitle className="text-3xl">{healthyPct}%</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Action bar */}
      <Card className="mb-6">
        <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Switch
              id="include-consistent-toggle"
              checked={includeConsistent}
              onCheckedChange={setIncludeConsistent}
              data-testid="ssot-include-consistent-toggle"
            />
            <label htmlFor="include-consistent-toggle" className="text-sm text-gray-700 select-none cursor-pointer">
              Include already-consistent rows
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={healing || divergedCount === 0}
              onClick={() => runHeal(true)}
              data-testid="ssot-dry-run-btn"
            >
              Dry run
            </Button>
            <Button
              disabled={healing || divergedCount === 0}
              onClick={() => setConfirmOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="ssot-heal-btn"
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              {divergedCount > 0 ? `Heal ${divergedCount} listing${divergedCount === 1 ? '' : 's'}` : 'All clean'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {rows.length} listing{rows.length === 1 ? '' : 's'}
            {includeConsistent ? '' : ' with divergence'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2">Listing</th>
                  <th className="px-4 py-2">Vendor</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 w-[360px]">Sums per path</th>
                  <th className="px-4 py-2">Will resolve to</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                      {includeConsistent
                        ? 'No listings with variants.'
                        : 'All variant matrices in sync 🎉'}
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-gray-100 hover:bg-gray-50/60"
                    data-testid={`ssot-row-${r.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.title || '—'}</div>
                      <div className="text-xs text-gray-400 font-mono">{r.id?.slice(0, 8)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700">{r.vendor_store_name || r.vendor_name || '—'}</div>
                      {r.previously_reconciled_at && (
                        <div className="text-[10px] text-indigo-500 mt-0.5">
                          Previously healed
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip status={r.status} />
                    </td>
                    <td className="px-4 py-3">
                      <SumsTable sums={r.sums_by_path} />
                    </td>
                    <td className="px-4 py-3">
                      {r.reconciled_total !== null ? (
                        <div>
                          <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                            {r.reconciled_total} units
                          </Badge>
                          {r.reconciled_source && (
                            <div className="text-[11px] text-gray-400 mt-1 font-mono">
                              src: {r.reconciled_source}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Heal confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Heal {divergedCount} diverged listing{divergedCount === 1 ? '' : 's'}?</DialogTitle>
            <DialogDescription className="text-left">
              This writes reconciled <code className="bg-gray-100 px-1 rounded text-xs">variant_combinations</code>
              {' '}to all four SSOT paths (root snake, root camel, nested snake, nested camel) plus mirrors the total
              to <code className="bg-gray-100 px-1 rounded text-xs">stock</code>, <code className="bg-gray-100 px-1 rounded text-xs">inventory_quantity</code>, and
              <code className="bg-gray-100 px-1 rounded text-xs"> data.inventory</code>.
              Takes the <strong>highest</strong> stock across paths so no real inventory is ever lost. Idempotent.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="ssot-heal-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={() => runHeal(false)}
              disabled={healing}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="ssot-heal-confirm-btn"
            >
              {healing ? 'Healing…' : 'Heal now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-heal report */}
      {healReport && !healing && (
        <Card className="mt-6" data-testid="ssot-heal-report">
          <CardHeader>
            <CardTitle className="text-base">
              {healReport.dry_run ? 'Dry run report' : 'Heal complete'}
            </CardTitle>
            <CardDescription>
              Scanned {healReport.scanned} · Healed {healReport.healed} · Skipped {healReport.skipped}
            </CardDescription>
          </CardHeader>
          {healReport.rows?.length > 0 && (
            <CardContent>
              <ul className="text-sm space-y-1 max-h-64 overflow-y-auto">
                {healReport.rows.slice(0, 100).map((r) => (
                  <li key={r.id} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle2 className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    <span className="truncate">{r.title}</span>
                    <span className="text-xs text-gray-400 ml-auto">→ {r.reconciled_total} units</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          )}
        </Card>
      )}
        </TabsContent>

        <TabsContent value="drift" className="mt-6">
          <AdminSSOTDriftWatchlist />
        </TabsContent>

        <TabsContent value="financial" className="mt-6">
          <AdminFinancialIntegrityPanel />
        </TabsContent>
      </Tabs>
    </div>
    </DashboardLayout>
  );
}
